import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  Close,
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from "@mui/icons-material";
import "./LoginPanel.css";
import { authAPI } from "../services/api";

const LoginPanel = ({ onLogin, onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    userName: "",
    password: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!formData.userName || !formData.password) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    try {
      // API call to your backend endpoint
      const response = await authAPI.login({
        userName: formData.userName,
        password: formData.password,
      });

      console.log("Login response:", response);

      // Store authentication data from backend response
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userName", response.data.user.username);
        localStorage.setItem("user", JSON.stringify(response.data.user));

        // Call onLogin with user data from backend response
        // Adjust these fields based on what your AuthenticationResponse contains
        onLogin(response.data.user);
      } else {
        setError("Login failed: No token received");
      }
    } catch (error) {
      console.error("Login error:", error);

      // Handle different error cases based on backend response
      if (error.response?.status === 403 || error.response?.status === 401) {
        // Your backend returns 403 for invalid credentials
        setError(
          "Invalid username or password. Please check your credentials."
        );
      } else if (error.response?.status === 400) {
        setError("Bad request. Please check your input.");
      } else if (error.response?.data?.message) {
        // Use the error message from your backend
        setError(error.response.data.message);
      } else if (error.code === "NETWORK_ERROR" || error.request) {
        setError("Network error. Please check your connection.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="login-dialog"
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: "relative", p: 4 }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "grey.500",
            }}
          >
            <Close />
          </IconButton>

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in to your MatLab account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              type="text"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter your username!"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Enter your password"
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              className="gradient-button"
              sx={{
                py: 1.5,
                borderRadius: 3,
                boxShadow: "0 8px 25px rgba(37, 99, 235, 0.3)",
                "&:hover": {
                  boxShadow: "0 12px 35px rgba(37, 99, 235, 0.4)",
                },
              }}
            >
              {isLoading ? "Signing In..." : "Sign In to Dashboard"}
            </Button>
          </form>

          <Box sx={{ mt: 3, p: 3, bgcolor: "grey.50", borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary" align="center">
              Secure access portal for authorized users. Enter your credentials
              to continue.
              <br />
              Contact support if you encounter issues.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoginPanel;
